import { isDefined } from "@clipboard-health/util-ts";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { type KeyboardEvent, useCallback, useState } from "react";

import { type SearchResultItem } from "./useOmnisearchResults";

function getIsMac(): boolean {
  return navigator.platform.toUpperCase().includes("MAC");
}

interface UseOmnisearchKeyboardParams {
  allResults: SearchResultItem[];
  selectedIndex: number;
  setSelectedIndex: (index: number | ((previous: number) => number)) => void;
  setExpandedShiftId: (id: string | null) => void;
  setExpandedWorkerId: (id: string | null) => void;
  setExpandedWorkplaceId: (id: string | null) => void;
  onSelect: (item: SearchResultItem) => void;
}

export function useOmnisearchKeyboard(params: UseOmnisearchKeyboardParams) {
  const {
    allResults,
    selectedIndex,
    setSelectedIndex,
    setExpandedShiftId,
    setExpandedWorkerId,
    setExpandedWorkplaceId,
    onSelect,
  } = params;
  const [copiedItem, setCopiedItem] = useState<"worker" | "workplace" | "shift" | "stripe" | null>(
    null
  );

  const handleCopySelectedId = useCallback(() => {
    const selectedItem = allResults[selectedIndex];

    if (selectedItem?.type === "agent-shift" || selectedItem?.type === "workplace-shift") {
      if (!isDefined(navigator.clipboard)) {
        showErrorToast("Unable to copy to clipboard");
        return false;
      }

      navigator.clipboard.writeText(selectedItem.data.id).then(
        () => {
          setCopiedItem("shift");
          setTimeout(() => {
            setCopiedItem(null);
          }, 2000);
        },
        () => {
          showErrorToast("Failed to copy to clipboard");
        }
      );

      return true;
    }

    if (selectedItem?.type === "agent-stripe") {
      if (!isDefined(navigator.clipboard)) {
        showErrorToast("Unable to copy to clipboard");
        return false;
      }

      navigator.clipboard.writeText(selectedItem.data.accountId).then(
        () => {
          setCopiedItem("stripe");
          setTimeout(() => {
            setCopiedItem(null);
          }, 2000);
        },
        () => {
          showErrorToast("Failed to copy to clipboard");
        }
      );

      return true;
    }

    if (selectedItem?.type !== "shift-action") {
      return false;
    }

    const id =
      selectedItem.action === "worker"
        ? selectedItem.data.agent?.userId
        : selectedItem.data.facility?.userId;

    if (!isDefined(id)) {
      return false;
    }

    if (!isDefined(navigator.clipboard)) {
      showErrorToast("Unable to copy to clipboard");
      return false;
    }

    navigator.clipboard.writeText(id).then(
      () => {
        setCopiedItem(selectedItem.action);
        setTimeout(() => {
          setCopiedItem(null);
        }, 2000);
      },
      () => {
        showErrorToast("Failed to copy to clipboard");
      }
    );

    return true;
  }, [allResults, selectedIndex]);

  const handleKeyDown = useCallback(
    // eslint-disable-next-line complexity -- keyboard handler requires multiple branches for different key combinations
    (event: KeyboardEvent) => {
      const selectedItem = allResults[selectedIndex];
      const isModifierKey = getIsMac() ? event.metaKey : event.ctrlKey;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((previous) => Math.min(previous + 1, Math.max(allResults.length - 1, 0)));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((previous) => Math.max(previous - 1, 0));
      } else if (
        (event.key === "ArrowRight" || event.key === "Tab") &&
        selectedItem?.type === "shift"
      ) {
        event.preventDefault();
        setExpandedShiftId(selectedItem.data.id);
      } else if (
        (event.key === "ArrowRight" || event.key === "Tab") &&
        selectedItem?.type === "agent" &&
        !selectedItem.expanded
      ) {
        event.preventDefault();
        setExpandedWorkerId(selectedItem.data.userId);
      } else if (
        (event.key === "ArrowRight" || event.key === "Tab") &&
        selectedItem?.type === "facility" &&
        !selectedItem.expanded
      ) {
        event.preventDefault();
        setExpandedWorkplaceId(selectedItem.data.userId);
      } else if (
        event.key === "ArrowLeft" &&
        (selectedItem?.type === "shift-action" ||
          (selectedItem?.type === "shift" && selectedItem.expanded))
      ) {
        event.preventDefault();
        setExpandedShiftId(null);
      } else if (
        event.key === "ArrowLeft" &&
        (selectedItem?.type === "agent-shift" ||
          selectedItem?.type === "agent-stripe" ||
          (selectedItem?.type === "agent" && selectedItem.expanded))
      ) {
        event.preventDefault();
        setExpandedWorkerId(null);
      } else if (
        event.key === "ArrowLeft" &&
        (selectedItem?.type === "workplace-shift" ||
          (selectedItem?.type === "facility" && selectedItem.expanded))
      ) {
        event.preventDefault();
        setExpandedWorkplaceId(null);
      } else if (event.key === "Enter" && allResults.length > 0) {
        event.preventDefault();
        if (isDefined(selectedItem)) {
          onSelect(selectedItem);
        }
      } else if (
        isModifierKey &&
        event.key === "c" &&
        (selectedItem?.type === "shift-action" ||
          selectedItem?.type === "agent-shift" ||
          selectedItem?.type === "agent-stripe" ||
          selectedItem?.type === "workplace-shift")
      ) {
        event.preventDefault();
        handleCopySelectedId();
      }
    },
    [
      allResults,
      selectedIndex,
      setSelectedIndex,
      setExpandedShiftId,
      setExpandedWorkerId,
      setExpandedWorkplaceId,
      onSelect,
      handleCopySelectedId,
    ]
  );

  return {
    handleKeyDown,
    handleCopySelectedId,
    copiedItem,
  };
}
